export const track = (name, props) =>
  analytics.track(name, {
    currentPageWhenTriggered: document.location.pathname,
    ...props
  });

// Debounce tracking for transfer_to_shop event to prevent duplicate firing
const recentTransfers = new Map();
const DEBOUNCE_WINDOW = 1000; // 1 second

/**
 * Extract affiliate partner from URL
 * @param {string} url - Affiliate URL
 * @returns {string} - Partner domain (e.g., 'weedmaps.com', 'leafly.com')
 */
export const extractAffiliatePartner = (url) => {
  if (!url) return 'unknown';
  
  try {
    const urlObj = new URL(url);
    // Remove 'www.' prefix if present
    return urlObj.hostname.replace(/^www\./, '');
  } catch (error) {
    console.warn('Invalid affiliate URL:', url);
    return 'unknown';
  }
};

/**
 * Track transfer_to_shop event (Key Event)
 * Fires when user clicks any outbound link to partner/retailer
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.affiliateUrl - Partner/retailer URL
 * @param {string} params.contentType - Type of page/content (article, product_page, homepage, etc.)
 * @param {string} params.surfaceType - UI surface where click occurred (product_card, deal_card, etc.)
 * @param {string} [params.productTitle] - Product name (optional)
 * @param {string} [params.category] - Product category (optional)
 * @param {string} [params.brand] - Brand name (optional)
 */
export const trackTransferToShop = ({
  affiliateUrl,
  contentType,
  surfaceType,
  productTitle = '',
  category = '',
  brand = ''
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Debounce: prevent duplicate firing within 1 second for same URL
  const now = Date.now();
  const lastFired = recentTransfers.get(affiliateUrl);
  
  if (lastFired && (now - lastFired) < DEBOUNCE_WINDOW) {
    console.log('transfer_to_shop debounced for:', affiliateUrl);
    return;
  }
  
  recentTransfers.set(affiliateUrl, now);
  
  // Clean up old entries after 2 seconds
  setTimeout(() => {
    recentTransfers.delete(affiliateUrl);
  }, DEBOUNCE_WINDOW * 2);

  const affiliatePartner = extractAffiliatePartner(affiliateUrl);

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'transfer_to_shop',
    affiliate_partner: affiliatePartner,
    content_type: contentType,
    surface_type: surfaceType,
    product_name: productTitle,
    category: category,
    brand: brand,
    destination_url: affiliateUrl,
    timestamp: new Date().toISOString()
  });

  console.log('transfer_to_shop tracked:', {
    affiliate_partner: affiliatePartner,
    content_type: contentType,
    surface_type: surfaceType,
    product_name: productTitle
  });
};

// Newsletter type mapping: Klaviyo/Mailchimp list IDs to readable names
export const NEWSLETTER_TYPES = {
  'SVQR9F': 'herb_hype',              // Primary newsletter (bi-weekly)
  'Ws5mPf': 'herb_trend_report',      // Monthly trend report
  'WjkfLi': 'article_newsletter',     // Article-specific newsletter
  'SJ47Zm': 'ilgm_partnership',       // ILGM seeds partnership
  'XsSXN2': 'california_newsletter',  // California-specific
  'T6MXJp': 'advent_calendar',        // Advent calendar entries
  'mailchimp_grow': 'growers_newsletter' // Mailchimp grower's newsletter
};

/**
 * Track generate_lead event (Key Event)
 * Fires ONLY when user successfully signs up for newsletter
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.method - Form location (homepage_inline, homepage_sticky, newsletter_page)
 * @param {string} params.newsletterType - Newsletter type (herb_hype, herb_trend_report, etc.)
 * @param {string} [params.listId] - Klaviyo/Mailchimp list ID (optional, for debugging)
 * @param {string} [params.email] - User email (optional, for debugging - do NOT send PII to GA4)
 */
export const trackGenerateLead = ({
  method,
  newsletterType,
  listId = '',
  email = ''
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Validate required parameters
  if (!method || !newsletterType) {
    console.warn('trackGenerateLead: Missing required parameters', { method, newsletterType });
    return;
  }

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'generate_lead',
    method: method,
    newsletter_type: newsletterType,
    list_id: listId, // For internal tracking, not sent to GA4
    timestamp: new Date().toISOString()
  });

  console.log('generate_lead tracked:', {
    method: method,
    newsletter_type: newsletterType,
    list_id: listId,
    // Note: email logged locally but NOT sent to GA4 (privacy)
    email_provided: email ? 'yes' : 'no'
  });
};

/**
 * Track search event (NOT a Key Event)
 * Fires when user submits an Algolia search query
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.searchTerm - The search query entered by user
 * @param {string} [params.searchType] - Type of search (product, article, deal, etc.)
 */
export const trackSearch = ({
  searchTerm,
  searchType = 'general'
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Validate required parameters
  if (!searchTerm) {
    console.warn('trackSearch: Missing search term');
    return;
  }

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'search',
    search_term: searchTerm,
    search_type: searchType,
    timestamp: new Date().toISOString()
  });

  console.log('search tracked:', {
    search_term: searchTerm,
    search_type: searchType
  });
};

/**
 * Track dispensary_search event (NOT a Key Event)
 * Fires when user searches for dispensaries by location
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.location - Location searched (city, state, zip, etc.)
 * @param {string} [params.radius] - Search radius (optional)
 */
export const trackDispensarySearch = ({
  location,
  radius = ''
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Validate required parameters
  if (!location) {
    console.warn('trackDispensarySearch: Missing location');
    return;
  }

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'dispensary_search',
    location: location,
    radius: radius,
    timestamp: new Date().toISOString()
  });

  console.log('dispensary_search tracked:', {
    location: location,
    radius: radius
  });
};

/**
 * Track select_promotion event (Key Event)
 * Fires when user clicks on a deal card or seasonal promo
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.promotionName - Name of the promotion/deal
 * @param {string} params.dealType - Type of deal (percentage_off, dollar_off, bogo, seasonal_420, seasonal_black_friday, etc.)
 * @param {string} [params.brand] - Brand name (optional)
 * @param {string} [params.category] - Product category (optional)
 */
export const trackSelectPromotion = ({
  promotionName,
  dealType,
  brand = '',
  category = ''
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Validate required parameters
  if (!promotionName || !dealType) {
    console.warn('trackSelectPromotion: Missing required parameters', { promotionName, dealType });
    return;
  }

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'select_promotion',
    promotion_name: promotionName,
    deal_type: dealType,
    brand: brand,
    category: category,
    timestamp: new Date().toISOString()
  });

  console.log('select_promotion tracked:', {
    promotion_name: promotionName,
    deal_type: dealType,
    brand: brand,
    category: category
  });
};

/**
 * Track select_item event (Key Event)
 * Fires when user clicks on a product card to view product details
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.itemName - Product name/title
 * @param {string} params.itemCategory - Product category
 * @param {string} params.surfaceType - UI surface where click occurred (product_listing, deal_listing, slider, sidebar_module, etc.)
 * @param {string} [params.brand] - Brand name (optional)
 */
export const trackSelectItem = ({
  itemName,
  itemCategory,
  surfaceType,
  brand = ''
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Validate required parameters
  if (!itemName || !itemCategory || !surfaceType) {
    console.warn('trackSelectItem: Missing required parameters', { itemName, itemCategory, surfaceType });
    return;
  }

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'select_item',
    item_name: itemName,
    item_category: itemCategory,
    surface_type: surfaceType,
    brand: brand,
    timestamp: new Date().toISOString()
  });

  console.log('select_item tracked:', {
    item_name: itemName,
    item_category: itemCategory,
    surface_type: surfaceType,
    brand: brand
  });
};

/**
 * Track view_item event (Key Event)
 * Fires when user lands on a product detail page
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.itemName - Product name/title
 * @param {string} params.itemCategory - Product category
 * @param {string} [params.affiliatePartner] - Affiliate partner if applicable
 * @param {string} [params.brand] - Brand name (optional)
 */
export const trackViewItem = ({
  itemName,
  itemCategory,
  affiliatePartner = '',
  brand = ''
}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Validate required parameters
  if (!itemName || !itemCategory) {
    console.warn('trackViewItem: Missing required parameters', { itemName, itemCategory });
    return;
  }

  // Push event to GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'view_item',
    item_name: itemName,
    item_category: itemCategory,
    affiliate_partner: affiliatePartner,
    brand: brand,
    timestamp: new Date().toISOString()
  });

  console.log('view_item tracked:', {
    item_name: itemName,
    item_category: itemCategory,
    affiliate_partner: affiliatePartner,
    brand: brand
  });
};

/**
 * Helper: Get newsletter type from list ID
 * @param {string} listId - Klaviyo or Mailchimp list ID
 * @returns {string} - Human-readable newsletter type
 */
export const getNewsletterType = (listId) => {
  return NEWSLETTER_TYPES[listId] || listId;
};

/**
 * Track pop-up funnel events (age gate, newsletter overlay)
 * Used to measure drop-off between the age gate and the newsletter pop-up
 *
 * @param {string} event - Event name (age_gate_view, age_gate_accept, age_gate_decline, newsletter_popup_view, newsletter_popup_close)
 * @param {Object} [params] - Extra event parameters
 */
export const trackPopupEvent = (event, params = {}) => {
  // Client-side only
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: event,
    page_path: window.location.pathname,
    ...params,
    timestamp: new Date().toISOString()
  });
};
